import React, { useState, useContext, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import classNames from 'classnames';
import { PrimaryButton, DefaultButton } from '@fluentui/react/lib/Button';
import { Panel } from '@fluentui/react/lib/Panel';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  DataServiceContext,
  InstrumentationServiceContext,
} from '@bingads-webui-campaign-react/smart-page-context';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { getParameter } from '@bingads-webui/url-util';

import { FacebookImportDialogStepFBConnect } from './components/facebook-import-dialog-step-fb-connect';
import { FacebookImportDialogStepFBImport } from './components/facebook-import-dialog-step-fb-import';
import { facebookInit, facebookLogin, facebookLogout } from './util/facebook-import-util';

const IMPORT_STEP = {
  connect: 0,
  pageList: 1,
};

const getStyles = () => ({
  panelContent: {
    marginTop: 20,
    paddingBottom: 65,
  },
  panelSubTitle: {
    marginBottom: 16,
  },
  btnNext: {
    marginRight: 8,
  },
});

const FB_ERRORS = {
  SignInError: 'SIGN_IN_ERR',
  GetPageListError: 'GET_PAGE_LIST_ERROR',
  GetPageError: 'GET_PAGE_INFO_ERROR',
};

const UnStyledSmartPageFacebookImportDialogInternal = ({
  i18n,
  classes,
  updateFBInfo,
  dataFilter,
  onDismiss,
  siteAuthInfo,
  setSiteAuthInfo,
  showWarningMessage,
  autoConnect,
}) => {
  const [fbSites, setFbSites] = useState([]);
  const [selectedFBSiteId, setSelectedFBSiteId] = useState('');
  const [step, setStep] = useState(-1);
  const [startStep, setStartStep] = useState(-1);
  const { smartPageService, hostSystemScenario, fbAppId } = useContext(DataServiceContext);
  const logger = useContext(InstrumentationServiceContext);

  const loadingTxtConfig = {
    Initiating: i18n.getString(_TL_('Initiating Facebook import...')),
    Importing: i18n.getString(_TL_('Importing your content over...')),
  };
  const [loading, setLoading] =
    useState({ isLoading: false, loadingTxt: loadingTxtConfig.Initiating });

  const handleFacebookSDKError = useCallback(() => {
    showWarningMessage(i18n.getString(_TL_('Failed to connect to Facebook server, please try again later.')));
  }, [i18n, showWarningMessage]);

  const handleErrors = (error) => {
    switch (error) {
      case FB_ERRORS.SignInError:
        showWarningMessage(i18n.getString(_TL_('We were not able to sign you into Facebook. Please try again.')));
        facebookLogout(_.noop, logger, fbAppId, handleFacebookSDKError);
        break;
      case FB_ERRORS.GetPageListError:
        showWarningMessage(i18n.getString(_TL_('We could not find any Facebook pages. Please try signing into your account again.')));
        facebookLogout(_.noop, logger, fbAppId, handleFacebookSDKError);
        break;
      case FB_ERRORS.GetPageError:
        showWarningMessage(i18n.getString(_TL_('We were not able to import the information from this Facebook page. Please try again.')));
        break;
      default:
        break;
    }
  };

  const handleFBPagePartialErrors = (errors) => {
    let haveError = false;
    _.each(errors, (error) => {
      if (!_.isEmpty(error)) {
        haveError = true;
      }
    });
    if (haveError) {
      showWarningMessage(i18n.getString(_TL_('We weren’t able to import everything necessary for your website. This may be due to some permission not being accepted or privacy settings on your Facebook page.')));
    }
  };

  const getFBPageListInfo = (fbInfo) => {
    if (!fbInfo || !fbInfo.authResponse || !fbInfo.authResponse.accessToken) {
      setLoading({ isLoading: false, loadingTxt: loadingTxtConfig.Initiating });
      handleErrors(FB_ERRORS.SignInError);
      return;
    }

    const authInfo = {
      fbToken: fbInfo.authResponse.accessToken,
      fbUserId: fbInfo.authResponse.userID,
    };

    setSiteAuthInfo(authInfo);
    smartPageService.getFacebookPageList(authInfo, [], hostSystemScenario)
      .then((data) => {
        if (_.isEmpty(data)) {
          handleErrors(FB_ERRORS.GetPageListError);
          return;
        }
        setFbSites(data);
        setStep(IMPORT_STEP.pageList);
      }).catch((err) => {
        handleErrors(FB_ERRORS.GetPageListError);
        logger.logTrace('FacebookImportDialog|GetPageListError', err);
      }).finally(() => {
        setLoading({ isLoading: false, loadingTxt: loadingTxtConfig.Initiating });
      });
  };

  const connectFacebookForSelenium = () => {
    const fbUserId = getParameter('fbUserId');
    const fbToken = getParameter('fbToken');
    const seleniumFBInfo = {
      authResponse: {
        accessToken: fbToken,
        userID: fbUserId,
      },
    };
    setLoading({ isLoading: true, loadingTxt: loadingTxtConfig.Initiating });
    getFBPageListInfo(seleniumFBInfo);
  };

  const connectFacebook = () => {
    setLoading({ isLoading: true, loadingTxt: loadingTxtConfig.Initiating });
    facebookLogin(getFBPageListInfo, (err) => {
      handleErrors(FB_ERRORS.SignInError);
      logger.logTrace('FacebookImportDialog|LoginError', err);
    }, logger, fbAppId, handleFacebookSDKError);
  };

  const connect = () => {
    const envPattern = /^(app1\.)?(ui\.)?(beta\.)?ads-int\.microsoft\.com$/;
    const isSISelenium = getParameter('isSelenium') && document.domain.match(envPattern);

    if (isSISelenium) {
      connectFacebookForSelenium();
    } else {
      connectFacebook();
    }
  };

  const onNextClick = () => {
    switch (step) {
      case IMPORT_STEP.connect:
        logger.logTrace('FacebookImportDialog|ConnectStep|ConnectButton|clicked');
        connect();
        break;
      case IMPORT_STEP.pageList:
        logger.logTrace('FacebookImportDialog|ImportStep|ImportButton|clicked');
        setLoading({ isLoading: true, loadingTxt: loadingTxtConfig.Importing });
        smartPageService
          .getFacebookPage(selectedFBSiteId, siteAuthInfo, dataFilter, hostSystemScenario)
          .then((fbSiteConfig) => {
            updateFBInfo(fbSiteConfig, siteAuthInfo.fbUserId);
            handleFBPagePartialErrors(fbSiteConfig.fbErrors);
          }).catch((err) => {
            handleErrors(FB_ERRORS.GetPageError);
            logger.logTrace('FacebookImportDialog|GetPageDetailError', err);
          }).finally(() => {
            setLoading({ isLoading: false, loadingTxt: loadingTxtConfig.Initiating });
          });
        break;
      default:
        break;
    }
  };

  const handleSetStartStep = (stepToSet) => {
    setStartStep(stepToSet);

    if (stepToSet === IMPORT_STEP.connect && autoConnect) {
      connect();
    }
  };

  const onPreviousClick = () => {
    if (step === 0) {
      return;
    }
    const newStep = step - 1;
    setStep(newStep);
  };

  const onCancelClick = () => {
    logger.logTrace('FacebookImportDialog|CancelButton|clicked');
    onDismiss();
  };

  const onReConnectFB = () => {
    facebookLogout(() => {
      setStep(IMPORT_STEP.connect);
      handleSetStartStep(IMPORT_STEP.connect);
    }, logger, fbAppId, handleFacebookSDKError);
  };

  useEffect(() => {
    facebookInit(fbAppId, logger, handleFacebookSDKError);
  }, [fbAppId, logger, handleFacebookSDKError]);

  useEffect(() => {
    smartPageService.getFacebookPageList(siteAuthInfo, [], hostSystemScenario).then((data) => {
      if (_.isEmpty(data)) {
        setStep(IMPORT_STEP.connect);
        handleSetStartStep(IMPORT_STEP.connect);
        return;
      }
      setFbSites(data);
      setStep(IMPORT_STEP.pageList);
      handleSetStartStep(IMPORT_STEP.pageList);
      setSiteAuthInfo({ ...siteAuthInfo, fbUserId: data[0].facebookUserId });
    }).catch((err) => {
      handleErrors(FB_ERRORS.GetPageListError);
      setStep(IMPORT_STEP.connect);
      logger.logTrace('FacebookImportDialog|GetPageListError', err);
    });
  }, []);

  const dialogSteps = [
    {
      title: i18n.getString(_TL_('Connect with Facebook')),
      subtitle: i18n.getString(_TL_('Please connect to your Facebook acccount or select your connected profile you would like to import your facebook page from.')),
      content: (
        <FacebookImportDialogStepFBConnect
          updateFBSites={setFbSites}
          onConnectClick={connect}
        />
      ),
    },
    {
      title: i18n.getString(_TL_('Import from Facebook')),
      content: (
        <FacebookImportDialogStepFBImport
          fbSites={fbSites}
          onFBSiteSelected={setSelectedFBSiteId}
          dataFilter={dataFilter}
          onReConnectFB={onReConnectFB}
        />),
    }];

  const onRenderFooterContent = () => {
    const nextBtnText = step === IMPORT_STEP.pageList ? i18n.getString(_TL_('Import')) : i18n.getString(_TL_('Next'));

    return (
      <div className={classes.panelFooter}>
        <PrimaryButton
          id="fb-import-panel-btn-next"
          disabled={loading.isLoading}
          className={classes.btnNext}
          onClick={onNextClick}
          text={nextBtnText}
        />
        {
          (step === startStep || autoConnect) ?
            <DefaultButton onClick={onCancelClick} text={i18n.getString(_TL_('Cancel'))} /> :
            <DefaultButton onClick={onPreviousClick} text={i18n.getString(_TL_('Previous'))} />
        }
      </div>
    );
  };

  useEffect(() => logger.logTrace('FacebookImportDialog|shown'), [logger]);

  return step === -1 ? null : (
    <Panel
      isOpen
      headerText={dialogSteps[step].title}
      onDismiss={onDismiss}
      onRenderFooterContent={onRenderFooterContent}
      isFooterAtBottom
      isBlocking
      className={classNames(classes.panel, 'smart-page-facebook-import-dialog')}
      closeButtonAriaLabel={i18n.getString(_TL_('Close'))}
    >
      <UiBlocker
        blocking={loading.isLoading}
        loadingText={loading.loadingTxt}
      >
        <div className={classes.panelContent}>
          <div className={classes.panelSubTitle}>
            {dialogSteps[step].subtitle}
          </div>
          {dialogSteps[step].content}
        </div>
      </UiBlocker>
    </Panel>
  );
};

UnStyledSmartPageFacebookImportDialogInternal.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  classes: PropTypes.shape({
    itemContainer: PropTypes.string,
  }).isRequired,
  updateFBInfo: PropTypes.func.isRequired,
  dataFilter: PropTypes.arrayOf(PropTypes.string),
  onDismiss: PropTypes.func,
  setSiteAuthInfo: PropTypes.func,
  siteAuthInfo: PropTypes.shape({
    fbToken: PropTypes.string,
    fbUserId: PropTypes.string,
  }),
  showWarningMessage: PropTypes.func,
  autoConnect: PropTypes.bool,
};

UnStyledSmartPageFacebookImportDialogInternal.defaultProps = {
  dataFilter: [],
  onDismiss: _.noop,
  showWarningMessage: _.noop,
  autoConnect: false,
  setSiteAuthInfo: _.noop,
  siteAuthInfo: { fbUserId: '', fbToken: '' },
};

export const SmartPageFacebookImportDialogInternal = withDefaultStyles(
  withI18nConsumer(UnStyledSmartPageFacebookImportDialogInternal),
  getStyles
);
